import { useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  IconButton,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  useScrollTrigger,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SECTIONS from "./sections";
import useActiveSection from "../../hooks/useActiveSection";
import { BRAND, UI } from "../../config/site";
import { scrollToId } from "../../lib/scroll";

export default function Navbar() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const trigger = useScrollTrigger({ threshold: 16, disableHysteresis: true });
  const [open, setOpen] = useState(false);
  const ids = useMemo(() => SECTIONS.map((s) => s.id), []);
  const active = useActiveSection(ids);

  return (
    <>
      <AppBar position="fixed" color="primary" elevation={trigger ? 6 : 2}>
        <Toolbar
          disableGutters
          sx={{ minHeight: mdUp ? UI.toolbar.md : UI.toolbar.xs }}
        >
          <Container
            maxWidth="lg"
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
          >
            <Box
              onClick={() => scrollToId("header")}
              sx={{ cursor: "pointer" }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  component="img"
                  src="/assets/images/logo.png"
                  alt="Logo"
                  sx={{ width: 32, height: 32 }}
                />
                <Box sx={{ fontWeight: 900, color: "common.white" }}>
                  {BRAND.name}
                </Box>
              </Stack>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {mdUp ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                {SECTIONS.map((item) => (
                  <Button
                    key={item.id}
                    onClick={() => scrollToId(item.id)}
                    size="small"
                    sx={(theme) => ({
                      mx: 0.25,
                      fontWeight: 700,
                      color:
                        active === item.id
                          ? theme.palette.warning.contrastText
                          : theme.palette.common.white,
                      opacity: active === item.id ? 1 : 0.85,
                      backgroundColor:
                        active === item.id
                          ? theme.palette.warning.main
                          : "transparent",
                      borderRadius: 5,
                      "&:hover": {
                        backgroundColor:
                          active === item.id
                            ? theme.palette.warning.main
                            : "rgba(255,255,255,0.1)",
                      },
                    })}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            ) : (
              <IconButton
                onClick={() => setOpen(true)}
                size="large"
                sx={{ color: "common.white" }}
                aria-label="فتح القائمة"
              >
                <MenuRoundedIcon />
              </IconButton>
            )}
          </Container>
        </Toolbar>
      </AppBar>

      {/* Spacer under fixed AppBar */}
      <Box sx={{ height: mdUp ? UI.toolbar.md : UI.toolbar.xs }} />

      {/* Drawer (Mobile) */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 300 } }}
      >
        <Box sx={{ p: 2, fontWeight: 900, color: "primary.main" }}>
          {BRAND.name}
        </Box>

        <List sx={{ pt: 0 }}>
          {SECTIONS.map((item) => {
            const isActive = active === item.id;
            return (
              <ListItemButton
                key={item.id}
                selected={isActive}
                onClick={() => {
                  scrollToId(item.id);
                  setOpen(false);
                }}
                sx={(theme) => ({
                  borderRadius: 1.5,
                  mx: 1,
                  my: 0.5,
                  // اللون الافتراضي
                  color: theme.palette.text.primary,
                  // عند التحديد (active)
                  "&.Mui-selected": {
                    bgcolor: theme.palette.warning.main,
                    color: theme.palette.warning.contrastText,
                  },
                  // ثبات اللون عند التحويم وهو محدد
                  "&.Mui-selected:hover": {
                    bgcolor: theme.palette.warning.main,
                  },
                  // تحويم عادي عند غير محدد
                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                  },
                })}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    sx: (theme) => ({
                      color: isActive
                        ? theme.palette.warning.contrastText
                        : theme.palette.text.primary,
                    }),
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>
    </>
  );
}
